/**
 * Transport hybride : le journal local reste l'autorité, le relais en est la copie.
 *
 * C'est le mode « les élèves votent depuis leur téléphone », vu du bureau de
 * l'enseignant :
 *
 *   - toute écriture part **d'abord en local** — l'enseignant ne dépend jamais
 *     du réseau pour continuer ;
 *   - une boucle de synchronisation pousse ensuite les événements que le
 *     serveur ignore, et rapatrie les bulletins déposés par les élèves ;
 *   - si le réseau tombe, la boucle échoue en silence et réessaiera : rien
 *     n'est perdu, rien n'est bloqué.
 *
 * Le serveur déduplique par identifiant d'événement : pousser deux fois le
 * même événement est sans effet, ce qui rend la reprise sûre.
 */

import { mergeOrder, type JournalEvent } from '../core/index.js';
import { ajouterEvenements, lireJournal } from './distant.js';
import type { LocalTransport } from './local.js';
import type { Transport } from './transport.js';

export interface OptionsHybride {
  readonly local: LocalTransport;
  readonly electionId: string;
  /** Empreinte du code enseignant : c'est elle qui authentifie les écritures. */
  readonly secret: string;
  /** Cadence d'interrogation. Dix secondes suffisent pour une salle de classe. */
  readonly intervalleMs?: number;
}

export class TransportHybride implements Transport {
  readonly kind = 'hybride';

  readonly #local: LocalTransport;
  readonly #electionId: string;
  readonly #secret: string;
  readonly #intervalleMs: number;

  #distants: JournalEvent[] = [];
  #signatureDistante = '';
  #ecouteurs = new Set<() => void>();
  #detachLocal: (() => void) | null = null;
  #minuteur: ReturnType<typeof setInterval> | null = null;
  #occupe = false;

  constructor(options: OptionsHybride) {
    this.#local = options.local;
    this.#electionId = options.electionId;
    this.#secret = options.secret;
    this.#intervalleMs = options.intervalleMs ?? 10_000;
  }

  /** Journal fusionné : local (autorité) puis distant, dédupliqué par identifiant. */
  async list(): Promise<JournalEvent[]> {
    const locaux = await this.#local.list();
    const vus = new Set(locaux.map((evenement) => evenement.id));
    const complement = this.#distants.filter((evenement) => !vus.has(evenement.id));
    return mergeOrder([...locaux, ...complement]);
  }

  async append(event: JournalEvent): Promise<void> {
    await this.#local.append(event);
    void this.#pousser();
  }

  async clear(): Promise<void> {
    await this.#local.clear();
    this.#distants = [];
  }

  subscribe(listener: () => void): () => void {
    this.#ecouteurs.add(listener);
    if (this.#detachLocal === null) {
      this.#detachLocal = this.#local.subscribe(() => this.#prevenir());
    }
    if (this.#minuteur === null) {
      // Premier cycle tout de suite : l'enseignant voit arriver les bulletins
      // sans attendre un intervalle complet.
      void this.synchroniser();
      this.#minuteur = setInterval(() => void this.synchroniser(), this.#intervalleMs);
    }
    return () => {
      this.#ecouteurs.delete(listener);
      if (this.#ecouteurs.size === 0) this.arreter();
    };
  }

  arreter(): void {
    if (this.#minuteur !== null) {
      clearInterval(this.#minuteur);
      this.#minuteur = null;
    }
    this.#detachLocal?.();
    this.#detachLocal = null;
  }

  /** Un cycle complet : pousser ce que le serveur ignore, puis tirer le reste. */
  async synchroniser(): Promise<void> {
    if (this.#occupe) return;
    this.#occupe = true;
    try {
      await this.#pousser();
      await this.#tirer();
    } finally {
      this.#occupe = false;
    }
  }

  async #pousser(): Promise<void> {
    try {
      const locaux = await this.#local.list();
      const connus = new Set(this.#distants.map((evenement) => evenement.id));
      const aPousser = locaux.filter((evenement) => !connus.has(evenement.id));
      if (aPousser.length === 0) return;
      await ajouterEvenements(this.#electionId, this.#secret, aPousser);
    } catch {
      // Réseau indisponible : le journal local reste complet, on réessaiera.
    }
  }

  async #tirer(): Promise<void> {
    try {
      const distants = mergeOrder(await lireJournal(this.#electionId, this.#secret));
      const signature = distants.map((evenement) => evenement.id).join(',');
      if (signature === this.#signatureDistante) return;
      this.#signatureDistante = signature;

      /*
        On **persiste** les événements distants dans le journal local.
        Sans cela, la copie de l'enseignant serait incomplète : si son
        navigateur se fermait, les bulletins reçus des élèves ne seraient plus
        nulle part. Son appareil doit détenir le journal entier.
      */
      const connus = new Set((await this.#local.list()).map((evenement) => evenement.id));
      for (const evenement of distants) {
        if (!connus.has(evenement.id)) await this.#local.append(evenement);
      }

      this.#distants = distants;
      this.#prevenir();
    } catch {
      // Idem : on garde ce que l'on a, la prochaine tentative rattrapera.
    }
  }

  #prevenir(): void {
    for (const ecouteur of this.#ecouteurs) ecouteur();
  }
}
